// AES-GCM encryption for localStorage data, keyed per uid via PBKDF2.
// The derived key is cached in memory for the session to avoid re-deriving on every read/write.

const keyCache = new Map(); // uid → CryptoKey

function saltStorageKey(uid) {
  return `myspend_salt_${uid}`;
}

async function getOrCreateSalt(uid) {
  const stored = localStorage.getItem(saltStorageKey(uid));
  if (stored) return Uint8Array.from(atob(stored), c => c.charCodeAt(0));
  const salt = crypto.getRandomValues(new Uint8Array(16));
  localStorage.setItem(saltStorageKey(uid), btoa(String.fromCharCode(...salt)));
  return salt;
}

async function getDerivedKey(uid) {
  if (keyCache.has(uid)) return keyCache.get(uid);
  const salt = await getOrCreateSalt(uid);
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(uid),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  const key = await crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: 100_000, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
  keyCache.set(uid, key);
  return key;
}

// Encrypt a plaintext string for the given uid.
// Returns a base64-encoded "IV.ciphertext" string.
export async function encryptForUser(uid, plaintext) {
  const key = await getDerivedKey(uid);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(plaintext),
  );
  const ivB64 = btoa(String.fromCharCode(...iv));
  const ctB64 = btoa(String.fromCharCode(...new Uint8Array(ciphertext)));
  return `${ivB64}.${ctB64}`;
}

// Decrypt a string produced by encryptForUser. Returns null on failure (wrong key / corrupted).
export async function decryptForUser(uid, encrypted) {
  try {
    const key = await getDerivedKey(uid);
    const [ivB64, ctB64] = encrypted.split('.');
    const iv = Uint8Array.from(atob(ivB64), c => c.charCodeAt(0));
    const ciphertext = Uint8Array.from(atob(ctB64), c => c.charCodeAt(0));
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
    return new TextDecoder().decode(decrypted);
  } catch {
    return null;
  }
}
